import React, { useEffect, useState } from "react";
import checkboxOnIcon from "core/icons/checkbox/checkbox-on.svg";
import checkboxOffIcon from "core/icons/checkbox/checkbox-off.svg";
import checkboxDisabledOnIcon from "core/icons/checkbox/checkbox-disabled-on.svg";
import checkboxDisabledOffIcon from "core/icons/checkbox/checkbox-disabled-off.svg";
import roundedCheckboxOffIcon from "core/icons/checkbox/rounded-checkbox-off.svg";
import roundedCheckboxOnIcon from "core/icons/checkbox/rounded-checkbox-on.svg";
import indeterminateCheckboxIcon from "core/icons/checkbox/checkbox-indeterminate.svg";
import indeterminateDisabledCheckboxIcon from "core/icons/checkbox/checkbox-indeterminate-disabled.svg";
import { SpacingStyle } from "core/styles/paddingMarginStyle";
// import { Markdown } from "../../../apps/vite-app/src/components/";
import { Container, Icon, Input, Label } from "./_checkboxStyle";
import { cn } from "core/utils/cn";

type Props = SpacingStyle & {
  name?: string;
  label?: React.ReactNode;
  checked?: boolean;
  width?: string | number;
  height?: string | number;
  disabled?: boolean;
  rightContent?: React.ReactNode;
  setFieldValue?: (field: any, checked?: boolean) => void;
  isCheckboxOnly?: boolean;
  ignoreDefaultClickHandler?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onClickLabel?: () => void;
  isRounded?: boolean;
  labelStyle?: object;
  "data-testid"?: string;
  isMarkdown?: boolean;
  isIndeterminate?: boolean;
  customCheckBockIcon?: { active: string; inActive: string };
  className?: string;
  iconClassName?: string;
};

function Checkbox(props: Props) {
  const {
    name,
    checked,
    disabled,
    label,
    rightContent,
    width,
    height,
    setFieldValue,
    isCheckboxOnly,
    ignoreDefaultClickHandler,
    "data-testid": dataTestId,
    isRounded,
    labelStyle,
    isMarkdown,
    isIndeterminate,
    customCheckBockIcon,
    onClickLabel = () => {},
    className,
    iconClassName,
    ...rest
  } = props;

  const [activeIcon, setActiveIcon] = useState(checkboxOffIcon);

  const onIcon = customCheckBockIcon?.active
    ? customCheckBockIcon?.active
    : isRounded
      ? roundedCheckboxOnIcon
      : checkboxOnIcon;
  const offIcon = customCheckBockIcon?.inActive
    ? customCheckBockIcon?.inActive
    : isRounded
      ? roundedCheckboxOffIcon
      : checkboxOffIcon;

  useEffect(() => {
    let icon = isIndeterminate ? indeterminateCheckboxIcon : checked ? onIcon : offIcon;
    if (disabled)
      icon = isIndeterminate
        ? indeterminateDisabledCheckboxIcon
        : checked
          ? checkboxDisabledOnIcon
          : checkboxDisabledOffIcon;
    setActiveIcon(icon);
  }, [checked, disabled, isIndeterminate]);

  const handleClick = () => {
    if (disabled || ignoreDefaultClickHandler) return;

    if (activeIcon === checkboxOffIcon || activeIcon === roundedCheckboxOffIcon) {
      setActiveIcon(isIndeterminate ? indeterminateCheckboxIcon : onIcon);
      setFieldValue && setFieldValue(name, true);
    } else {
      setActiveIcon(isIndeterminate ? indeterminateCheckboxIcon : offIcon);
      setFieldValue && setFieldValue(name, false);
    }
  };

  if (isCheckboxOnly) {
    return (
      <>
        <Input
          type="checkbox"
          checked={checked}
          onClick={handleClick}
          onChange={(e) => {}}
          data-testid={dataTestId}
          disabled={disabled}
        />
        <Icon
          onClick={handleClick}
          disabled={disabled}
          src={activeIcon}
          width={width}
          height={height}
          className={cn("checkbox-icon", iconClassName)}
          {...rest}
        />
      </>
    );
  }

  return (
    <Container className={cn("checkbox-container", className)} {...rest}>
      <Input
        type="checkbox"
        checked={checked}
        onClick={handleClick}
        data-testid={dataTestId}
        onChange={(e) => {}}
        disabled={disabled}
      />
      <Icon
        data-testid={dataTestId + "icon"}
        onClick={handleClick}
        disabled={disabled}
        src={activeIcon}
        width={activeIcon === checkboxDisabledOnIcon ? width - 2 : width}
        height={activeIcon === checkboxDisabledOnIcon ? height - 2 : height}
        className={cn("checkbox-icon", iconClassName)}
      />
      {isMarkdown ? null : (
        // <Markdown>{label}</Markdown>
        <Label
          onClick={onClickLabel}
          className="checkbox-label"
          disabled={disabled}
          style={labelStyle}>
          {label}
        </Label>
      )}
      {rightContent}
    </Container>
  );
}

Checkbox.defaultProps = {
  name: "",
  label: "",
  rightContent: null,
  checked: false,
  disabled: false,
  setFieldValue: () => {},
  isCheckboxOnly: false,
  ignoreDefaultClickHandler: false,
  isRounded: false,
};

export default Checkbox;
